<template>
  <CCard
    class="overflow-x-scroll"
    :class="[paginationData?.total > 5 ? 'p-5' : 'px-5 pt-5 pb-2']"
    v-if="mounted"
  >
    <CTableWrapper
      :title="t('coins_count', { count: paginationData?.total })"
      :data="tableData"
      :head="coinsHead"
      :items-per-page="paginationData?.defaultLimit ?? 0"
      :limit="paginationData?.defaultLimit ?? 0"
      :total="paginationData?.total"
      :current-page="paginationData?.currentPage ?? 0"
      th-class="!text-left"
      @itemsPerPage="onChangeLimit"
      :loading="loading"
      no-search
      no-footer
      @pageChange="onPageChange"
      @openSingle="openSingle"
      :body-tr-class="'transition-all hover:!bg-gray-250/60 cursor-pointer'"
    >
      <template #_index="{ row: data }">
        <span class="font-semibold">{{ data?._index }}</span>
      </template>
      <template #currency="{ row: data }">
        <CCardCoin :card="data" :pair="data?.pair" />
      </template>
      <template #amount="{ row: data }">
        <p>
          {{ formatMoneyExact(data?.amount, 4) }}
        </p>
      </template>
      <template #stake_amount="{ row: data }">
        <p>
          {{ formatMoneyExact(data?.stake_amount, 2) }}
        </p>
      </template>
      <template #open_rate="{ row: data }">
        <p>
          {{ formatMoneyExact(data?.open_rate, 4) }}
        </p>
      </template>
      <template #current_rate="{ row: data }">
        <p>
          {{ formatMoneyExact(data?.current_rate, 4) }}
        </p>
      </template>
      <template #current_profit="{ row: data }">
        <CCoinProfit :abs="data?.profit_abs" :pct="data?.profit_pct" />
      </template>
      <template #sell="{ row: data }">
        <button
          @click.stop="sellCoin(data?.freqtrade_trade_id, data?.pair)"
          class="bg-gray/10 p-2 rounded group hover:bg-gray/10 transition-200"
        >
          <span
            class="icon-moneys text-dark/80 text-lg transition-200 group-hover:text-green"
          />
        </button>
      </template>
      <template #open_date="{ row: data }">
        <p>{{ convertToUserTimezone(data?.open_date) }}</p>
      </template>
      <template #no-data>
        <CNodata
          main-class="min-h-[202px]"
          :title="$t('no_active_trades_title')"
          :subtitle="$t('no_active_trades_subtitle')"
          image="/images/svg/no-data/no-bots.svg"
        />
      </template>
      <template #filter>
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2 mb-4">
          <CProfileDashDetail
            class="!border-solid w-full"
            v-for="(detail, index) in detailInfo"
            :key="index"
            :title="detail?.value"
            :loading="loading"
            :description="detail?.title"
          />
        </div>
      </template>
    </CTableWrapper>
  </CCard>
</template>

<script setup lang="ts">
import { coinsHead } from "@/modules/Bots/data";
import CCard from "@/components/Card/CardWrapper.vue";
import CTableWrapper from "@/components/Common/Table/CTableWrapper.vue";
import CNodata from "@/components/Common/CNodata.vue";
import CCoinProfit from "@/modules/Bots/components/CCoinProfit.vue";
import CCardCoin from "@/modules/Bots/components/CCardCoin.vue";
import CProfileDashDetail from "@/components/Profile/CProfileDashDetail.vue";
import router from "@/router";
import { useI18n } from "vue-i18n";
import { useMounted } from "@/composables/useMounted";
import { formatMoneyExact } from "@/utils";
import { useConvertToUserTimezone } from "@/composables/useConvertToUserTimezone";
import { useRoute } from "vue-router";

interface Props {
  tableData: any;
  loading: boolean;
  paginationData: any;
  detailInfo: {
    title: string;
    value: number | string;
  }[];
}
defineProps<Props>();
const emit = defineEmits<{
  (e: "onChangeLimit"): void;
  (e: "onPageChange"): void;
  (e: "sellCoin", coin: number, pair: string): void;
}>();

const { convertToUserTimezone } = useConvertToUserTimezone();
const { mounted } = useMounted();
const { t } = useI18n();
const route = useRoute();

const openSingle = (data: any) => {
  const locale = route.path.split("/")[1];

  router.push({
    name: `${locale}-BotsCoinSingle`,
    params: {
      coinId: data?.id,
      botId: route.params.botId,
    },
  });
};

const onChangeLimit = () => {
  emit("onChangeLimit");
};
const onPageChange = () => {
  emit("onPageChange");
};
const sellCoin = (coin: number, pair: string) => {
  emit("sellCoin", coin, pair);
};
</script>

<style scoped></style>
